import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { removeSpecialCharacters } from "../../utils";
import style from "./apptopicbanner.module.scss";
import {
  selectDitaPath,
  selectHeadTitle,
  selectTocId,
} from "../../features/counter/counterSlice";
import classNames from "classnames";
import Headline from "./Headline";
import TopBar from "./TopBar";
import { Helmet } from "react-helmet";
import { useFindInPage } from "../../hooks/FindInPageHooks/useFindInPage";
import FindInPageModal from "../FindInPage/FindInPageModal";
import TableOfContendsModal from "../TableOfContents/TableOfContendsModal";
import TablesAndFiguresModal from "../TablesAndFigures/TablesAndFiguresModal";
import { useTableOfContents } from "../../hooks/tableOfContentsHooks/useTableOfContents";
import { useOpenInModal } from "../../hooks/OpenInModalHooks/useOpenInModal";
import AnimatedPopup from "../AnimatedComponents/AnimatedPopup";
import useMobileOrientation from "../../hooks/useMobileOrientation";
import useAppModernisation from "../../hooks/useAppModernisation";
import TopBarWeb from "./TopBarWeb";
import { mpstopicfile } from "../../utils/guidelines";
import ShareSlidePopup from "../ShareButton/ShareSlidePopup";
import useShareTopic from "../../hooks/shareTopicHooks/useShareTopic";
import { useFontSizeAdjustor } from "../../hooks/fontSizeAdjustorHooks/useFontSizeAdjustor";
import FontSizeAdjustorModal from "../FontSizeAdjustor/FontSizeAdjustorModal";

function getCleanGuidelinePage(searchParams) {
  const guidelinePage = searchParams.get("guidelinePage");
  let cleanGuidelinePage = removeSpecialCharacters(guidelinePage);
  return cleanGuidelinePage ? cleanGuidelinePage.replace(/ /g, "_") : "";
}

function determineGuidelinePath(search, pathname, ditapath, mpstopicfile) {
  const searchParams = new URLSearchParams(search);
  if (pathname.includes("viewTopic") || pathname.includes("topicTeaser")) {
    const cleanDitapath = removeSpecialCharacters(ditapath);
    if (cleanDitapath === "") {
      return;
    }
    let cleanGuidelinePage = getCleanGuidelinePage(searchParams);

    if (cleanGuidelinePage === "" || cleanDitapath === cleanGuidelinePage) {
      if (mpstopicfile[cleanDitapath]) {
        return cleanDitapath;
      } else {
        console.error(
          `No object found in mpstopicfile for ditapath: ${ditapath}`,
        );
      }
    } else if (cleanDitapath !== cleanGuidelinePage) {
      return cleanGuidelinePage.replace("/", "");
    }
  }
}

function getBannerText(headTitle, tocId, pathname) {
  const bannerMapping = {
    "/viewTopic/": headTitle || tocId,
    "/viewTopic": headTitle || tocId,
  };

  for (const [key, value] of Object.entries(bannerMapping)) {
    if (pathname === key || pathname.includes(key)) {
      return value;
    }
  }
}

const AppTopicBanner = ({
  pathname,
  search,
  hash,
  expandExpertGroupsSideBar,
  expandUpdatesSideBar,
  setShowTopicActionBar,
}) => {
  const bannerRef = useRef(null);
  const findInPage = useFindInPage();
  const tableOfContents = useTableOfContents();
  const openInModal = useOpenInModal();
  const shareModal = useShareTopic();
  const fontSizeAdjustor = useFontSizeAdjustor();
  const [backgroundColourClass, setBackgroundColourClass] = useState("");
  const [topicDetails, setTopicDetails] = useState("");
  const [guideline, setGuideline] = useState("");
  const [themeColour, setThemeColour] = useState("");
  const [showHeader, setShowHeader] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showBannerText, setShowBannerText] = useState(false);
  const ditapath = useSelector(selectDitaPath);
  const headTitle = useSelector(selectHeadTitle);
  const tocId = useSelector(selectTocId);
  const { isPortrait } = useMobileOrientation();
  const { isAppMode, isMobileView } = useAppModernisation();
  const msPath = `${search}${hash ? hash : ""}`;

  const topicBarClasses = classNames(
    style.topicBar,
    backgroundColourClass,
    "semibold",
  );

  useEffect(() => {
    const guidelinePath = determineGuidelinePath(
      search,
      pathname,
      ditapath,
      mpstopicfile,
    );
    const topicDetailsFromMps = mpstopicfile[guidelinePath];
    setTopicDetails(topicDetailsFromMps);
    setGuideline(guidelinePath);
    setBackgroundColourClass(style[guidelinePath]);
  }, [ditapath, pathname, search]);

  const bannerText = showBannerText
    ? getBannerText(headTitle, tocId, pathname)
    : " ";

  useEffect(() => {
    if (bannerRef.current) {
      const backgroundColor = window
        .getComputedStyle(bannerRef.current)
        .getPropertyValue("background-color");
      setThemeColour(backgroundColor);
    }
  }, [backgroundColourClass]);

  const controlHeader = () => {
    if (!isAppMode) return;
    const currentScroll = document.querySelector(".animated-route").scrollTop;

    if (findInPage.isOpen) {
      return;
    }
    if (currentScroll - 30 > lastScrollY || currentScroll === 0) {
      setShowHeader(false);
      setLastScrollY(currentScroll);
    } else if (currentScroll + 30 < lastScrollY) {
      setShowHeader(true);
      setLastScrollY(currentScroll);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBannerText(true);
    }, 500); // Avoid the flickering
    return () => clearTimeout(timer);
  }, []);

  if (isAppMode) {
    // Add the handler to all the different ways to scroll
    window.addEventListener("scroll", controlHeader);
    document.body.addEventListener("touchmove", controlHeader);
    document.addEventListener("wheel", controlHeader);
  }

  return (
    <>
      <div
        className={classNames(style.header, {
          [style.hiddenHeader]: !showHeader && !findInPage.isOpen,
        })}
        data-testid="appTopicBanner"
      >
        {(!isMobileView || isPortrait) && (
          <nav ref={bannerRef} role={"banner"} className={topicBarClasses}>
            <Helmet>
              <meta name="theme-color" content={themeColour} />
            </Helmet>
            {isAppMode ? (
              <div className={style.topicHeaderContainer}>
                <TopBar
                  topicTitle={headTitle}
                  topicUrl={pathname + msPath}
                  guideline={guideline}
                  expandExpertGroupsSideBar={expandExpertGroupsSideBar}
                  expandUpdatesSideBar={expandUpdatesSideBar}
                  msPath={msPath}
                  setShowTopicActionBar={setShowTopicActionBar}
                />
                <Headline topicDetails={topicDetails} bannerText={bannerText} />
              </div>
            ) : (
              <TopBarWeb
                topicTitle={headTitle}
                topicUrl={pathname + msPath}
                guideline={guideline}
                topicDetails={topicDetails}
                bannerText={bannerText}
                expandExpertGroupsSideBar={expandExpertGroupsSideBar}
                expandUpdatesSideBar={expandUpdatesSideBar}
                msPath={msPath}
                setShowTopicActionBar={setShowTopicActionBar}
              />
            )}
          </nav>
        )}
      </div>
      {findInPage.isOpen && (
        <FindInPageModal
          value={findInPage.value}
          onChange={findInPage.updateSearchTerm}
        />
      )}
      <AnimatedPopup show={tableOfContents.isOpen}>
        <TableOfContendsModal msPath={msPath}></TableOfContendsModal>
      </AnimatedPopup>
      <AnimatedPopup show={openInModal.isOpen}>
        <TablesAndFiguresModal
          title={openInModal.title}
          sectionId={openInModal.sectionId}
        ></TablesAndFiguresModal>
      </AnimatedPopup>
      <AnimatedPopup show={isMobileView && shareModal.isOpen}>
        <ShareSlidePopup />
      </AnimatedPopup>
      <AnimatedPopup show={isMobileView && fontSizeAdjustor.isOpen}>
        <FontSizeAdjustorModal />
      </AnimatedPopup>
    </>
  );
};

export default AppTopicBanner;
